"use client"

import useSWR from "swr"

type CutoverModeResponse = {
    cutoverMode: boolean
}

const cutoverModeFetcher = async (url: string): Promise<CutoverModeResponse> => {
    const res = await fetch(url, { cache: "no-store" })
    if (!res.ok) throw new Error(`Cutover mode fetch failed: ${res.status}`)
    return (await res.json()) as CutoverModeResponse
}

/**
 * Reads the server-authoritative `BASKET_CUTOVER_MODE` flag at runtime via `/api/cutover-mode`.
 *
 * The flag MUST be read from the server (not a `NEXT_PUBLIC_*` env var): `NEXT_PUBLIC_*` values
 * are inlined at build time, so they can never reflect a flag flip during the deploy window.
 * The `/api/cutover-mode` route reads `getCutoverMode()` server-side and responds `no-store`,
 * so flips propagate within seconds.
 *
 * All consumers share one SWR key (`/api/cutover-mode`) → one request + one focus listener
 * across every instance on the page (PDP add-to-cart, header, checkout).
 */
export default function useCutoverMode(): boolean {
    const { data } = useSWR<CutoverModeResponse>(
        "/api/cutover-mode",
        cutoverModeFetcher,
        {
            revalidateOnFocus: true,
            revalidateOnReconnect: true,
            dedupingInterval: 30_000
        }
    )

    return data?.cutoverMode === true
}
